import { useState, type ComponentPropsWithRef, type ReactNode } from "react";
import { AnimatedCopyIcon, AnimatedEyeIcon } from "@one-nexty/icons";

import { cn } from "../../lib/cn";
import { Badge, type BadgeProps } from "../badge";
import { Button } from "../button";
import { Input } from "../input";

export type EnvEnvironment = "production" | "preview" | "development";

export interface EnvVariable {
  id: string;
  key: string;
  value: string;
  isSecret?: boolean;
  environments: EnvEnvironment[];
  comment?: string;
}

export interface EnvVarEditorProps extends Omit<ComponentPropsWithRef<"div">, "onChange" | "title"> {
  variables: EnvVariable[];
  onChange?: (variables: EnvVariable[]) => void;
  readOnly?: boolean;
  title?: ReactNode;
  description?: ReactNode;
}

const ENV_LABELS: Record<EnvEnvironment, { label: string; tone: BadgeProps["tone"] }> = {
  production: { label: "Production", tone: "danger" },
  preview: { label: "Preview", tone: "warning" },
  development: { label: "Development", tone: "brand" },
};

/**
 * Composant de gestion des variables d'environnement et secrets pour console Cloud.
 *
 * ── Garanties UX & Sécurité ───────────────────────────────────────────────
 * 1. Valeurs masquées par défaut (`••••••••`) pour empêcher les fuites visuelles.
 * 2. Masque à dégradé d'opacité progressive (`bg-gradient-to-r`) sous les boutons
 *    d'action pour que le texte long disparaisse en douceur sous le fondu.
 * 3. Repose à 100% sur les composants Button & Badge du design system.
 */
export function EnvVarEditor({
  variables,
  onChange,
  readOnly = false,
  title = "Variables d'environnement",
  description = "Gérez les clés de configuration et les secrets de vos déploiements.",
  className,
  ...props
}: EnvVarEditorProps) {
  const [revealedIds, setRevealedIds] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const toggleReveal = (id: string) => {
    setRevealedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyValue = (id: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUpdate = (id: string, field: keyof EnvVariable, val: unknown) => {
    if (readOnly || !onChange) return;
    const updated = variables.map((v) => (v.id === id ? { ...v, [field]: val } : v));
    onChange(updated);
  };

  const handleAddRow = () => {
    if (readOnly || !onChange) return;
    const newVar: EnvVariable = {
      id: `env-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      key: "",
      value: "",
      isSecret: true,
      environments: ["production", "preview", "development"],
    };
    onChange([...variables, newVar]);
  };

  const handleRemoveRow = (id: string) => {
    if (readOnly || !onChange) return;
    onChange(variables.filter((v) => v.id !== id));
  };

  const toggleEnvironment = (id: string, env: EnvEnvironment) => {
    if (readOnly || !onChange) return;
    const item = variables.find((v) => v.id === id);
    if (!item) return;

    const hasEnv = item.environments.includes(env);
    const updatedEnvs = hasEnv
      ? item.environments.filter((e) => e !== env)
      : [...item.environments, env];

    handleUpdate(id, "environments", updatedEnvs);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-xl border border-border bg-surface-raised p-5 shadow-xs",
        className,
      )}
      {...props}
    >
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-fg">{title}</h3>
          {description && <p className="text-xs text-fg-muted mt-0.5">{description}</p>}
        </div>

        {!readOnly && (
          <Button size="sm" variant="secondary" onClick={handleAddRow}>
            + Ajouter une variable
          </Button>
        )}
      </div>

      {/* Liste des variables */}
      <div className="divide-y divide-border rounded-lg border border-border bg-surface-sunken/40">
        {variables.length === 0 ? (
          <div className="p-6 text-center text-xs text-fg-subtle">
            Aucune variable d'environnement configurée.
          </div>
        ) : (
          variables.map((item) => {
            const isRevealed = revealedIds[item.id];
            const isCopied = copiedId === item.id;

            return (
              <div
                key={item.id}
                className="flex flex-col gap-3 p-4 transition-colors hover:bg-surface-raised/40 sm:flex-row sm:items-center sm:justify-between"
              >
                {/* Clé & Valeur */}
                <div className="grid flex-1 grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <Input
                    value={item.key}
                    disabled={readOnly}
                    onChange={(e) => handleUpdate(item.id, "key", e.target.value)}
                    placeholder="EX: DATABASE_URL"
                    className="font-mono text-xs uppercase"
                    aria-label="Nom de la variable"
                  />

                  <div className="relative flex items-center overflow-hidden rounded-md">
                    <Input
                      type={item.isSecret && !isRevealed ? "password" : "text"}
                      value={item.value}
                      disabled={readOnly}
                      onChange={(e) => handleUpdate(item.id, "value", e.target.value)}
                      placeholder="Valeur ou secret"
                      className={cn("font-mono text-xs w-full", item.isSecret ? "pr-36" : "pr-20")}
                      aria-label={`Valeur pour ${item.key}`}
                    />

                    {/* Fondu d'opacité progressive en dégradé large sous les boutons d'action */}
                    <div
                      className="pointer-events-none absolute right-1 top-1 bottom-1 w-52 rounded-r-md bg-gradient-to-r from-transparent via-surface-raised/50 via-25% via-surface-raised/95 via-65% to-surface-raised"
                      aria-hidden="true"
                    />

                    {/* Boutons d'action superposés avec icônes animées */}
                    <div className="absolute right-1.5 flex items-center gap-1 z-10">
                      {item.isSecret && (
                        <Button
                          type="button"
                          size="icon-xs"
                          variant="ghost"
                          onClick={() => toggleReveal(item.id)}
                          title={isRevealed ? "Masquer la valeur" : "Afficher la valeur"}
                          aria-label={isRevealed ? "Masquer la valeur" : "Afficher la valeur"}
                        >
                          <AnimatedEyeIcon visible={isRevealed} size={15} />
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        onClick={() => copyValue(item.id, item.value)}
                        title={isCopied ? "Copié dans le presse-papier !" : "Copier la valeur"}
                        aria-label="Copier la valeur"
                      >
                        <AnimatedCopyIcon copied={isCopied} size={15} />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Scopes d'environnements */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {(["production", "preview", "development"] as EnvEnvironment[]).map((env) => {
                    const isActive = item.environments.includes(env);
                    const cfg = ENV_LABELS[env];

                    if (readOnly) {
                      return (
                        <Badge
                          key={env}
                          tone={isActive ? cfg.tone : "neutral"}
                          size="sm"
                          className={cn(!isActive && "opacity-40")}
                        >
                          {cfg.label}
                        </Badge>
                      );
                    }

                    return (
                      <button
                        key={env}
                        type="button"
                        onClick={() => toggleEnvironment(item.id, env)}
                        className="cursor-pointer focus-visible:outline-2 focus-visible:outline-ring rounded-full"
                      >
                        <Badge
                          tone={isActive ? cfg.tone : "neutral"}
                          size="sm"
                          className={cn(
                            "transition-all",
                            !isActive && "opacity-35 hover:opacity-75 bg-transparent border border-border",
                          )}
                        >
                          {cfg.label}
                        </Badge>
                      </button>
                    );
                  })}

                  {!readOnly && (
                    <Button
                      type="button"
                      size="icon-xs"
                      variant="ghost"
                      onClick={() => handleRemoveRow(item.id)}
                      className="ml-1 text-fg-subtle hover:text-danger-fg"
                      title="Supprimer la variable"
                      aria-label={`Supprimer ${item.key}`}
                    >
                      ✕
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
